import { copyToClipboard } from 'quasar'
import { TranslateResult } from 'vue-i18n'
import { useUi } from '../modules/ui/uiModule'
import { translate } from './translator'

/**
 * The message and error dialogs out of `CommonBaseMixin`, plus copy-to-clipboard, which reports
 * through them.
 *
 * Quasar 2 dialogs take no `parent`, and the dialog component itself comes off `$bs`, so this needs the
 * component instance rather than just `$q` — the same shape as AuthorBase's `useDialogs($q)`, one
 * level up. The mixin passes `this`; a `setup()` component passes `getCurrentInstance()!.proxy`.
 */

/**
 * No `$t`/`$tc` here any more, deliberately.
 *
 * These dialogs are opened from promise callbacks, and a callback can arrive after its component has
 * gone -- leaving a page while a request is in flight is all it takes. vue-i18n 9's `unmounted` hook
 * DELETES `$t`/`$tc` from the instance, so `host.$tc(...)` threw "host.$tc is not a function" out of
 * the error handler, which is uncaught and took the page down (in dev, behind webpack's overlay, which
 * then covered everything the next test tried to click). `translate` goes to the app-level instance,
 * which outlives any component. See [[feedback_setup_runs_before_created]] for the other half of this.
 */
export interface DialogHost {
  $q: { dialog (opts: any): unknown }
  $bs: { dialogComponent: unknown }
}

/** Anything with a `select()`: the native input or textarea, not the q-field markup around it. */
const isSelectable = (el: unknown): el is HTMLInputElement | HTMLTextAreaElement =>
  typeof (el as { select?: unknown } | null)?.select === 'function'

export function useDialogs (host: DialogHost) {
  const showMessage = (message: string | TranslateResult, ...optionalParams: any[]): void => {
    host.$q.dialog({
      component: host.$bs.dialogComponent,
      // Quasar 2 passes a dialog component its props through `componentProps`; at the
      // top level they are dialog options and never reach the component.
      componentProps: {
        heading: translate('system.actions.messageDialogHeading'),
        message: message.toString().concat(optionalParams.join(' '))
      }
    })
  }

  /**
   * De-duplicated through the ui store's `lastError`, cleared after two seconds: a failed save can
   * surface the same message from several requests at once, and the visitor should see one dialog.
   *
   * 'Unauthorized' is swallowed because the login prompt is already going up for it.
   */
  const showError = (message: string | TranslateResult, ...optionalParams: any[]) => {
    if (message === 'Unauthorized') {
      return
    }

    if (useUi().ui.store.getters.lastError !== message) {
      useUi().ui.setLastError(message)
      host.$q.dialog({
        component: host.$bs.dialogComponent,
        // Quasar 2 passes a dialog component its props through `componentProps`; at the
        // top level they are dialog options and never reach the component.
        componentProps: {
          error: true,
          heading: translate('system.actions.errorDialogHeading'),
          message: message?.toString().concat(optionalParams.join(' '))
        }
      })

      setTimeout(() => {
        useUi().ui.setLastError('')
      }, 2000)
    }
  }

  const toClipboard = (text: any) => {
    copyToClipboard(text).then(() => {
      showMessage(translate('system.labels.copiedToClipboard'))
    }).catch(() => {
      showError(translate('system.labels.couldNotCopyToClipboard'))
    })
  }

  /**
   * Focus an input and select everything in it. Lives here beside `toClipboard` because it is the other
   * half of the same affordance — all five templates that use it put the two side by side, so the reader
   * can copy the value the button also copies.
   *
   * The click can land anywhere on the field. Vue 3 binds `@click` on a component to its root, so a click on
   * the field's padding arrives with a `<div>` as its target, and `select()` on that threw (sc-28743) — so
   * unless the click was on the native input itself, it is found inside the element the listener sits on.
   */
  const selectInput = (e: Pick<Event, 'target' | 'currentTarget'>) => {
    const field = isSelectable(e.target)
      ? e.target
      : (e.currentTarget as Element | null)?.querySelector<HTMLInputElement>('input, textarea')

    if (!field) {
      return
    }

    field.focus()
    field.select()
  }

  return {
    showMessage,
    showError,
    toClipboard,
    selectInput
  }
}
